import { useEffect, useState } from 'react';
import { Button, Select, Input, DataTable, Column, Badge, StatCard, EmptyState, Chip, Card, ConfirmDialog, useToast } from '../../../../shared/components';
import { TrendIcon, ReportIcon, DownloadIcon, EyeIcon, EyeOffIcon, TrashIcon } from '../../../../shared/icons';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { ImportBatch, SnapshotDetail, ValidationError, MONTH_NAMES_ES } from '../../../../shared/services/results/types';
import { ImportResultsModal } from './ImportResultsModal';
import { ValidationErrorsTable } from './ValidationErrorsTable';

const repo = getResultsRepository();
const money = (v: number) => v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

/** Orden cronológico (año desc, luego mes desc dentro del año) - NO alfabético ni por fecha de creación del registro, para que el mes más reciente quede siempre primero/por defecto. */
const sortBatchesChronologically = (list: ImportBatch[]) =>
  [...list].sort((a, b) => b.sourceYear - a.sourceYear || MONTH_NAMES_ES.indexOf(b.sourceMonth.toLowerCase()) - MONTH_NAMES_ES.indexOf(a.sourceMonth.toLowerCase()));

interface GridRow {
  key: string;
  rowType: SnapshotDetail['rowType'];
  tipoCuenta: SnapshotDetail['tipoCuenta'];
  accountCode: string;
  accountDescription: string;
  values: Record<string, number>;
}

/**
 * Pestaña "Resultados del Mes" - equivalente a la hoja de Ingresos/Egresos
 * que envía Contabilidad. Permite cargar el .xlsx y muestra la grilla tal
 * cual viene el archivo: columna de Sección (INGRESOS/EGRESOS) al inicio,
 * las filas TOTAL INGRESOS/TOTAL EGRESOS/UTILIDAD conservadas al final de
 * cada bloque, y el panel "Por revisar" con los datos dudosos detectados.
 */
export function SnapshotTab() {
  const toast = useToast();
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [snapshot, setSnapshot] = useState<SnapshotDetail[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [view, setView] = useState<'grid' | 'errors'>('grid');
  const [showIngresos, setShowIngresos] = useState(false);
  const [showEgresos, setShowEgresos] = useState(false);
  const [filterCliente, setFilterCliente] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadBatches = async (preselectId?: string) => {
    // Se ordena cronológicamente (no por fecha de creación del registro) y se
    // preselecciona el mes más reciente, así el director ve el mes actual
    // apenas entra en vez de tener que buscarlo en la lista.
    const list = sortBatchesChronologically(await repo.listBatches());
    setBatches(list);
    const next = preselectId ?? (list[0]?.id ?? '');
    setSelectedId(next);
  };
  useEffect(() => {
    loadBatches().then(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSnapshot([]);
      setErrors([]);
      return;
    }
    Promise.all([repo.listSnapshotDetail(selectedId), repo.listValidationErrors(selectedId)]).then(([s, e]) => {
      setSnapshot(s);
      setErrors(e);
    });
  }, [selectedId]);

  const selectedBatch = batches.find((b) => b.id === selectedId) ?? null;

  const doDeleteMonth = async () => {
    if (!selectedBatch) return;
    setIsDeleting(true);
    try {
      await repo.deleteMonthProcessing(selectedBatch.id);
      toast.show(`Se eliminó todo lo procesado de ${selectedBatch.sourceMonth} ${selectedBatch.sourceYear} - ya puedes volver a cargar el archivo.`);
      setConfirmingDelete(false);
      await loadBatches();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo eliminar el procesamiento.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const allClientColumns = Array.from(new Set(snapshot.filter((s) => !s.isTotalColumn).map((s) => s.clientColumn))).sort();
  const clientColumns = filterCliente ? [filterCliente] : allClientColumns;

  const matchesSearch = (r: { accountCode: string; accountDescription: string }) => {
    if (!filterSearch.trim()) return true;
    const q = filterSearch.trim().toLowerCase();
    return r.accountCode.toLowerCase().includes(q) || r.accountDescription.toLowerCase().includes(q);
  };

  const groupBy = (rowType: SnapshotDetail['rowType'], tipoCuenta: SnapshotDetail['tipoCuenta']) => {
    const keys = Array.from(new Set(snapshot.filter((s) => s.rowType === rowType && s.tipoCuenta === tipoCuenta).map((s) => `${s.accountCode}::${s.accountDescription}`)));
    return keys.map((key): GridRow => {
      const [accountCode, accountDescription] = key.split('::');
      const values: Record<string, number> = {};
      for (const s of snapshot) if (s.rowType === rowType && s.tipoCuenta === tipoCuenta && s.accountCode === accountCode && s.accountDescription === accountDescription) values[s.clientColumn] = s.value;
      return { key: `${rowType}-${tipoCuenta}-${accountCode}`, rowType, tipoCuenta, accountCode, accountDescription, values };
    });
  };
  const utilidadRow: GridRow[] = snapshot.some((s) => s.rowType === 'UTILIDAD')
    ? [
        {
          key: 'utilidad',
          rowType: 'UTILIDAD',
          tipoCuenta: null,
          accountCode: 'UTILIDAD',
          accountDescription: 'Utilidad del mes',
          values: Object.fromEntries(snapshot.filter((s) => s.rowType === 'UTILIDAD').map((s) => [s.clientColumn, s.value])),
        },
      ]
    : [];

  // Orden igual al Excel: cuentas de ingresos, total ingresos, cuentas de egresos, total egresos, utilidad.
  // Las filas de cuenta se filtran por búsqueda; las filas de TOTAL/UTILIDAD quedan siempre visibles (dan contexto).
  const gridRows: GridRow[] = [
    ...groupBy('ACCOUNT', 'INGRESO').filter(matchesSearch),
    ...groupBy('SECTION_TOTAL', 'INGRESO'),
    ...groupBy('ACCOUNT', 'GASTOS').filter(matchesSearch),
    ...groupBy('SECTION_TOTAL', 'GASTOS'),
    ...utilidadRow,
  ];

  /** Suma local de respaldo, por si el mes fue importado antes de que se empezaran a guardar las filas TOTAL del archivo. */
  const sumByClient = (rows: GridRow[]): Record<string, number> => {
    const values: Record<string, number> = {};
    for (const c of allClientColumns) values[c] = rows.reduce((s, r) => s + (r.values[c] ?? 0), 0);
    return values;
  };

  // Datos para las dos tablas independientes (Ingresos / Egresos) con su propio Total al final,
  // tal como pediste que se viera "así como viene el Excel" - separadas, cada una con su cierre.
  const ingresoAccountRowsAll = groupBy('ACCOUNT', 'INGRESO');
  const ingresoAccountRows = ingresoAccountRowsAll.filter(matchesSearch);
  const ingresoTotalValues = groupBy('SECTION_TOTAL', 'INGRESO')[0]?.values ?? sumByClient(ingresoAccountRowsAll);
  const egresoAccountRowsAll = groupBy('ACCOUNT', 'GASTOS');
  const egresoAccountRows = egresoAccountRowsAll.filter(matchesSearch);
  const egresoTotalValues = groupBy('SECTION_TOTAL', 'GASTOS')[0]?.values ?? sumByClient(egresoAccountRowsAll);

  const rowStyle = (r: GridRow) => (r.rowType === 'ACCOUNT' ? '' : 'font-semibold bg-ink-50/60');

  const columns: Column<GridRow>[] = [
    {
      key: 'seccion',
      header: 'Sección',
      render: (r) => (r.rowType === 'UTILIDAD' ? <Badge tone="info">UTILIDAD</Badge> : <Badge tone={r.tipoCuenta === 'INGRESO' ? 'success' : 'critical'}>{r.tipoCuenta === 'INGRESO' ? 'INGRESOS' : 'EGRESOS'}</Badge>),
    },
    { key: 'accountCode', header: 'Cuenta', render: (r) => <span className={rowStyle(r)}>{r.rowType === 'ACCOUNT' ? r.accountCode : ''}</span> },
    { key: 'accountDescription', header: 'Descripción', render: (r) => <span className={rowStyle(r)}>{r.accountDescription}</span> },
    ...clientColumns.map((c): Column<GridRow> => ({ key: c, header: c, render: (r) => <span className={rowStyle(r)}>{money(r.values[c] ?? 0)}</span> })),
  ];

  const monthOptions = batches.map((b) => ({ value: b.id, label: `${b.sourceMonth.toUpperCase()} ${b.sourceYear}` }));

  return (
    // Excepción deliberada al layout móvil-first del resto de la Suite (ver
    // "Tabla Resumen" para el mismo patrón): esta pestaña se muestra a todo
    // lo ancho de la pantalla en computador.
    <div className="relative left-1/2 -ml-[50vw] w-screen space-y-4 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end gap-3">
        <Select label="Mes cargado" placeholder="Sin meses cargados" options={monthOptions} value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="max-w-[240px]" />
        <Button leftIcon={<DownloadIcon width={18} height={18} />} onClick={() => setImporting(true)}>
          Cargar resultados del mes
        </Button>
        {selectedBatch && (
          <Button variant="outline" leftIcon={<TrashIcon width={18} height={18} />} onClick={() => setConfirmingDelete(true)}>
            Eliminar procesamiento y volver a cargarlo
          </Button>
        )}
      </div>

      {!isLoading && batches.length === 0 && <EmptyState icon="📊" title="Sin resultados cargados" description='Usa "Cargar resultados del mes" para subir el archivo mensual de Ingresos/Egresos.' />}

      {selectedBatch && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatCard icon={<TrendIcon />} label="Total Ingresos" value={money(selectedBatch.totalIngresos)} tone="brand" />
            <StatCard icon={<TrendIcon />} label="Total Gastos" value={money(selectedBatch.totalGastos)} tone="critical" />
            <StatCard icon={<ReportIcon />} label="Utilidad" value={money(selectedBatch.utilidad)} tone={selectedBatch.utilidad >= 0 ? 'info' : 'critical'} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="Cliente"
              options={[{ value: '', label: '(Todos - muestra las 19 columnas)' }, ...allClientColumns.map((c) => ({ value: c, label: c }))]}
              value={filterCliente}
              onChange={(e) => setFilterCliente(e.target.value)}
            />
            <Input label="Buscar cuenta (código o descripción)" placeholder="ej. transporte, 41450502..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} />
          </div>

          <div className="flex gap-2">
            <Chip label="Grilla del mes" active={view === 'grid'} onClick={() => setView('grid')} />
            <Chip label={`Por revisar${errors.length ? ` (${errors.length})` : ''}`} active={view === 'errors'} onClick={() => setView('errors')} />
          </div>

          {view === 'grid' ? (
            <>
              <DataTable columns={columns} rows={gridRows} rowKey={(r) => r.key} pageSize={5} pageSizeOptions={[5, 10, 15, 30, 60]} emptyTitle="Sin cuentas" scrollClassName="scroll-accent" />

              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" leftIcon={showIngresos ? <EyeOffIcon width={18} height={18} /> : <EyeIcon width={18} height={18} />} onClick={() => setShowIngresos((v) => !v)}>
                  {showIngresos ? 'Ocultar' : 'Mostrar'} tabla de Ingresos
                </Button>
                <Button variant="outline" leftIcon={showEgresos ? <EyeOffIcon width={18} height={18} /> : <EyeIcon width={18} height={18} />} onClick={() => setShowEgresos((v) => !v)}>
                  {showEgresos ? 'Ocultar' : 'Mostrar'} tabla de Egresos
                </Button>
              </div>

              {showIngresos && <SectionTable title="Ingresos" tone="success" accountRows={ingresoAccountRows} totalValues={ingresoTotalValues} clientColumns={clientColumns} />}
              {showEgresos && <SectionTable title="Egresos" tone="critical" accountRows={egresoAccountRows} totalValues={egresoTotalValues} clientColumns={clientColumns} />}
            </>
          ) : (
            <ValidationErrorsTable errors={errors} />
          )}
        </>
      )}

      <ImportResultsModal open={importing} onClose={() => setImporting(false)} onImported={(result) => loadBatches(result.batch.id)} />

      <ConfirmDialog
        open={confirmingDelete}
        title={`Eliminar procesamiento de ${selectedBatch?.sourceMonth ?? ''} ${selectedBatch?.sourceYear ?? ''}`}
        description='Esto borra por completo el "Resultados del Mes", los datos pendientes de "Datos Tabla" y las filas "Por revisar" de este mes. Si el mes ya se había procesado, también se elimina del histórico final. Después de esto podrás cargar el archivo desde cero.'
        tone="danger"
        confirmLabel="Eliminar y permitir recargar"
        isLoading={isDeleting}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={doDeleteMonth}
      />
    </div>
  );
}

/**
 * Tabla independiente para una sola sección (Ingresos o Egresos), tal como
 * viene separado en el Excel. El cuerpo hace scroll si hay muchas cuentas
 * (Egresos suele tener ~140), pero la fila de Total queda SIEMPRE visible al
 * pie (sticky), en la MISMA tabla que las cuentas, para que sus columnas
 * queden exactamente alineadas con las de cada cliente.
 */
function SectionTable({
  title,
  tone,
  accountRows,
  totalValues,
  clientColumns,
}: {
  title: string;
  tone: 'success' | 'critical';
  accountRows: GridRow[];
  totalValues: Record<string, number>;
  clientColumns: string[];
}) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-ink-100 px-4 py-3">
        <Badge tone={tone}>{title.toUpperCase()}</Badge>
        <span className="text-sm text-ink-500">{accountRows.length} cuentas</span>
      </div>

      <div className="scroll-accent max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-100 bg-ink-50/60 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
              <th className="sticky top-0 z-10 whitespace-nowrap bg-ink-50/95 px-4 py-3 backdrop-blur">Cuenta</th>
              <th className="sticky top-0 z-10 whitespace-nowrap bg-ink-50/95 px-4 py-3 backdrop-blur">Descripción</th>
              {clientColumns.map((c) => (
                <th key={c} className="sticky top-0 z-10 whitespace-nowrap bg-ink-50/95 px-4 py-3 text-right backdrop-blur">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accountRows.length === 0 ? (
              <tr>
                <td colSpan={2 + clientColumns.length} className="px-4 py-6 text-center text-sm text-ink-400">
                  Sin cuentas en esta sección.
                </td>
              </tr>
            ) : (
              accountRows.map((r) => (
                <tr key={r.key} className="border-b border-ink-50 last:border-0 hover:bg-ink-50/40">
                  <td className="whitespace-nowrap px-4 py-3 text-ink-700">{r.accountCode}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-ink-700">{r.accountDescription}</td>
                  {clientColumns.map((c) => (
                    <td key={c} className="whitespace-nowrap px-4 py-3 text-right text-ink-700">
                      {money(r.values[c] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="sticky bottom-0 z-10 whitespace-nowrap bg-ink-100 px-4 py-3 font-bold text-ink-900 backdrop-blur">
                Total {title}
              </td>
              {clientColumns.map((c) => (
                <td key={c} className="sticky bottom-0 z-10 whitespace-nowrap bg-ink-100 px-4 py-3 text-right font-semibold text-ink-900 backdrop-blur">
                  {money(totalValues[c] ?? 0)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
